import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import SharedNavigation from "@/components/SharedNavigation";
import { optimizeImage } from "@/lib/mediaUrls";

// Phase 15.6P — RGM Reports redesigned as a magazine/news experience:
// featured hero article, category nav, trending stories rail, latest grid,
// related stories. Cover art, artist profile photos, and category accents
// throughout.

const CATEGORIES = [
  { key: "all", label: "All News", emoji: "📰" },
  { key: "winners", label: "Winners", emoji: "🏆" },
  { key: "announcements", label: "Announcements", emoji: "📢" },
  { key: "verified", label: "Verified", emoji: "✅" },
  { key: "spotlight", label: "Spotlights", emoji: "🌟" },
  { key: "producers", label: "Producers", emoji: "🎹" },
  { key: "creators", label: "Creators", emoji: "🎥" },
  { key: "crews", label: "Crews", emoji: "👥" },
  { key: "platform", label: "Platform", emoji: "🛰️" },
  { key: "magazine", label: "Magazine", emoji: "📖" },
];

const CATEGORY_COLORS = {
  winners: { chip: "bg-amber-500/20 text-amber-200 border-amber-500/40", glow: "from-amber-500/20" },
  announcements: { chip: "bg-fuchsia-500/20 text-fuchsia-200 border-fuchsia-500/40", glow: "from-fuchsia-500/20" },
  verified: { chip: "bg-sky-500/20 text-sky-200 border-sky-500/40", glow: "from-sky-500/20" },
  spotlight: { chip: "bg-purple-500/20 text-purple-200 border-purple-500/40", glow: "from-purple-500/20" },
  producers: { chip: "bg-pink-500/20 text-pink-200 border-pink-500/40", glow: "from-pink-500/20" },
  creators: { chip: "bg-rose-500/20 text-rose-200 border-rose-500/40", glow: "from-rose-500/20" },
  crews: { chip: "bg-emerald-500/20 text-emerald-200 border-emerald-500/40", glow: "from-emerald-500/20" },
  platform: { chip: "bg-cyan-500/20 text-cyan-200 border-cyan-500/40", glow: "from-cyan-500/20" },
  magazine: { chip: "bg-indigo-500/20 text-indigo-200 border-indigo-500/40", glow: "from-indigo-500/20" },
};

const formatDate = (d) => {
  if (!d) return "—";
  try {
    const date = new Date(d);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return String(d).slice(0, 10);
  }
};

// ====== Article meta (artist avatar + category chip + date) ======
const ArticleMeta = ({ report, size = "sm" }) => {
  const cat = CATEGORY_COLORS[report.category] || { chip: "bg-purple-500/20 text-purple-200 border-purple-500/40" };
  const avatarSize = size === "lg" ? "w-10 h-10" : size === "md" ? "w-8 h-8" : "w-6 h-6";
  const txt = size === "lg" ? "text-sm" : "text-xs";
  return (
    <div className={`flex items-center gap-2 ${txt} text-gray-400`}>
      <span className={`uppercase tracking-wider px-2 py-0.5 rounded-full border font-bold text-[10px] ${cat.chip}`}>
        {report.category}
      </span>
      {report.source_user_id && (
        <Link
          to={`/artist/${report.source_user_id}`}
          onClick={(e) => e.stopPropagation()}
          className="flex items-center gap-1.5 hover:text-white transition-colors"
          data-testid={`report-author-${report.report_id}`}
        >
          {report.source_user_photo ? (
            <img
              src={report.source_user_photo}
              alt={report.source_user_name}
              className={`${avatarSize} rounded-full object-cover border border-purple-500/40`}
              onError={(e) => { e.target.style.display = "none"; }}
            />
          ) : (
            <span className={`${avatarSize} rounded-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center text-white font-bold text-[10px] border border-purple-500/40`}>
              {(report.source_user_name || "?").charAt(0).toUpperCase()}
            </span>
          )}
          <span className="font-semibold truncate max-w-[140px]">{report.source_user_name}</span>
          {report.source_user_verified && <span className="text-sky-400 text-[10px]">✓</span>}
        </Link>
      )}
      <span className="text-gray-600">·</span>
      <span>{formatDate(report.published_at || report.created_at)}</span>
    </div>
  );
};

// ====== FEATURED HERO — magazine-style top story ======
const FeaturedHero = ({ report }) => {
  if (!report) return null;
  const cat = CATEGORY_COLORS[report.category] || { glow: "from-purple-500/20" };
  return (
    <Link
      to={`/reports/${report.report_id}`}
      className="group block mb-10"
      data-testid={`report-hero-${report.report_id}`}
    >
      <article className="relative overflow-hidden rounded-3xl border border-purple-500/30 bg-gradient-to-br from-gray-950 via-gray-900 to-black grid md:grid-cols-2 gap-0">
        {/* Cover image */}
        <div className="relative aspect-[16/10] md:aspect-auto md:min-h-[420px] overflow-hidden">
          {report.cover_image ? (
            <img
              src={optimizeImage(report.cover_image, 'cover')}
              alt={report.title}
              className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700"
            />
          ) : (
            <div className={`w-full h-full bg-gradient-to-br ${cat.glow} via-purple-900/20 to-black flex items-center justify-center text-7xl opacity-40`}>
              📰
            </div>
          )}
          {/* Banner overlay */}
          <div className="absolute top-4 left-4 flex items-center gap-2">
            <span className="px-3 py-1 rounded-full bg-amber-500 text-black text-[10px] font-black uppercase tracking-widest shadow-lg animate-pulse">
              ★ Featured Story
            </span>
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent md:bg-gradient-to-r" />
        </div>

        {/* Article body */}
        <div className="p-6 md:p-10 flex flex-col justify-between">
          <div>
            <ArticleMeta report={report} size="lg" />
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-white mt-4 leading-tight tracking-tight group-hover:text-purple-200 transition-colors">
              {report.title}
            </h1>
            {report.description && (
              <p className="text-gray-300 text-base mt-4 leading-relaxed line-clamp-4">
                {report.description}
              </p>
            )}
          </div>
          <div className="mt-6 flex items-center gap-3">
            <span className="text-purple-300 font-semibold text-sm group-hover:text-pink-300 transition-colors">
              Read the full story →
            </span>
          </div>
        </div>
      </article>
    </Link>
  );
};

// ====== TRENDING STORY (compact horizontal rail card) ======
const TrendingStoryCard = ({ report, rank }) => {
  const cat = CATEGORY_COLORS[report.category] || { glow: "from-purple-500/20" };
  return (
    <Link
      to={`/reports/${report.report_id}`}
      className="group flex-shrink-0 w-72 snap-start"
      data-testid={`report-trending-${report.report_id}`}
    >
      <div className="relative aspect-[4/3] rounded-2xl overflow-hidden border border-purple-500/20 group-hover:border-purple-500/50 transition-colors">
        {report.cover_image ? (
          <img src={optimizeImage(report.cover_image, 'card')} alt={report.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className={`w-full h-full bg-gradient-to-br ${cat.glow} via-gray-900 to-black flex items-center justify-center text-5xl opacity-50`}>📰</div>
        )}
        {rank && (
          <span className="absolute top-2 left-2 w-7 h-7 rounded-full bg-black/80 text-amber-300 font-black text-sm flex items-center justify-center border border-amber-500/40">
            {rank}
          </span>
        )}
      </div>
      <div className="mt-3 px-1">
        <ArticleMeta report={report} size="sm" />
        <h3 className="text-white font-bold text-base mt-1.5 leading-snug line-clamp-2 group-hover:text-purple-200 transition-colors">
          {report.title}
        </h3>
      </div>
    </Link>
  );
};

// ====== LATEST GRID CARD ======
const LatestStoryCard = ({ report }) => {
  const cat = CATEGORY_COLORS[report.category] || { glow: "from-purple-500/20" };
  return (
    <Link
      to={`/reports/${report.report_id}`}
      className="group block rounded-2xl border border-gray-800 bg-gray-950/60 overflow-hidden hover:border-purple-500/40 transition-all"
      data-testid={`report-card-${report.report_id}`}
    >
      <div className="relative aspect-video overflow-hidden">
        {report.cover_image ? (
          <img src={optimizeImage(report.cover_image, 'card')} alt={report.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className={`w-full h-full bg-gradient-to-br ${cat.glow} via-gray-900 to-black flex items-center justify-center text-4xl opacity-50`}>📰</div>
        )}
      </div>
      <div className="p-4">
        <ArticleMeta report={report} size="sm" />
        <h3 className="text-white font-bold text-lg mt-2 leading-snug line-clamp-2 group-hover:text-purple-200 transition-colors">
          {report.title}
        </h3>
        {report.description && (
          <p className="text-gray-400 text-sm mt-2 leading-relaxed line-clamp-2">
            {report.description}
          </p>
        )}
      </div>
    </Link>
  );
};

// ====== RELATED STORIES (mini list at bottom) ======
const RelatedStoryRow = ({ report }) => {
  const cat = CATEGORY_COLORS[report.category] || { chip: "bg-purple-500/20 text-purple-200 border-purple-500/40" };
  return (
    <Link
      to={`/reports/${report.report_id}`}
      className="flex items-center gap-3 p-3 rounded-xl bg-gray-900/40 border border-gray-800 hover:border-purple-500/40 hover:bg-gray-900/60 transition-all group"
      data-testid={`report-related-${report.report_id}`}
    >
      <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-purple-900/20">
        {report.cover_image ? (
          <img src={optimizeImage(report.cover_image, 'card')} alt={report.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-2xl opacity-50">📰</div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border font-bold ${cat.chip}`}>
          {report.category}
        </span>
        <h4 className="text-white font-semibold text-sm mt-1 line-clamp-2 group-hover:text-purple-200 transition-colors">
          {report.title}
        </h4>
        <p className="text-gray-500 text-[11px] mt-0.5">{formatDate(report.published_at || report.created_at)}</p>
      </div>
    </Link>
  );
};

// ====== Re-exported for backwards compatibility (used elsewhere) ======
export const ReportCard = ({ report, compact = false }) => {
  if (compact) return <RelatedStoryRow report={report} />;
  return <LatestStoryCard report={report} />;
};

// ====== MAIN PAGE ======
const ReportsPage = () => {
  const [user, setUser] = useState(null);
  const [category, setCategory] = useState("all");
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const me = await api.get("/auth/me");
        setUser(me.data);
      } catch { /* anon ok */ }
    })();
  }, []);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/reports", { params: { category, limit: 40 } });
      setReports(r.data.reports || []);
    } catch { setReports([]); }
    finally { setLoading(false); }
  }, [category]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  // Magazine layout buckets
  const { heroStory, trendingStories, latestStories, relatedStories } = useMemo(() => {
    // Hero = first featured (or newest)
    const hero = reports.find((r) => r.is_featured) || reports[0] || null;
    const rest = reports.filter((r) => r.report_id !== hero?.report_id);
    // Trending = next 6 (featured rest + recent)
    const trending = rest.slice(0, 6);
    // Latest grid = the next chunk
    const latest = rest.slice(6, 6 + 12);
    // Related = remainder, capped at 8 (acts as the "more stories" sidebar/strip)
    const related = rest.slice(6 + 12, 6 + 12 + 8);
    return {
      heroStory: hero,
      trendingStories: trending,
      latestStories: latest,
      relatedStories: related,
    };
  }, [reports]);

  return (
    <div className="min-h-screen bg-[#08080c]" data-testid="reports-page">
      <SharedNavigation currentUser={user} />

      {/* Magazine masthead */}
      <header className="pt-24 pb-6 px-4 sm:px-6 lg:px-8 border-b border-gray-900 bg-gradient-to-b from-purple-950/20 via-black to-transparent">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-3">
                <span className="text-xs uppercase tracking-[0.3em] text-amber-300 font-bold">RGM Studio</span>
                <span className="w-1.5 h-1.5 rounded-full bg-amber-300/60" />
                <span className="text-xs uppercase tracking-[0.3em] text-purple-300 font-bold">Issue · {new Date().toLocaleDateString("en-US", { month: "short", year: "numeric" })}</span>
              </div>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white tracking-tight mt-2 leading-none" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                📰 RGM Reports
              </h1>
              <p className="text-gray-400 mt-3 text-sm sm:text-base max-w-2xl">
                Platform news, contest winners, spotlights, and the artists shaping RGM — straight from the newsroom.
              </p>
            </div>
            {user && (user.role === "admin" || user.role === "ar" || user.role === "moderator") && (
              <Link to="/admin/reports">
                <Button className="bg-gradient-to-r from-purple-600 to-pink-600 text-white" data-testid="admin-post-report-btn">
                  + Post a Report
                </Button>
              </Link>
            )}
          </div>

          {/* Category navigation strip (newsroom-style) */}
          <nav className="mt-6 flex gap-1 overflow-x-auto pb-2 -mx-1 px-1 snap-x" data-testid="reports-category-nav">
            {CATEGORIES.map((c) => {
              const active = category === c.key;
              return (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => setCategory(c.key)}
                  className={`whitespace-nowrap px-4 py-2 text-sm font-bold border-b-2 transition-colors snap-start ${
                    active
                      ? "border-amber-400 text-white"
                      : "border-transparent text-gray-400 hover:text-white hover:border-purple-500/40"
                  }`}
                  data-testid={`reports-category-${c.key}`}
                >
                  <span className="mr-1">{c.emoji}</span>
                  {c.label}
                </button>
              );
            })}
          </nav>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {loading ? (
          <div className="text-center py-20 text-gray-400" data-testid="reports-loading">
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-t-2 border-purple-500" />
            <p className="mt-4 text-sm uppercase tracking-widest">Loading the newsroom…</p>
          </div>
        ) : reports.length === 0 ? (
          <Card className="p-12 text-center text-gray-400 bg-gray-900/40 border-gray-800" data-testid="reports-empty">
            <div className="text-6xl mb-4">📰</div>
            <p className="text-lg font-semibold text-white">No stories yet in this section.</p>
            <p className="text-sm mt-2">Check back soon — new editions drop weekly.</p>
          </Card>
        ) : (
          <>
            {/* ======= FEATURED HERO ======= */}
            <FeaturedHero report={heroStory} />

            {/* ======= TRENDING STORIES RAIL ======= */}
            {trendingStories.length > 0 && (
              <section className="mb-12" data-testid="reports-trending-section">
                <div className="flex items-end justify-between mb-5">
                  <h2 className="text-2xl md:text-3xl font-black text-white tracking-tight flex items-center gap-3">
                    <span className="inline-block w-1 h-8 bg-gradient-to-b from-amber-400 to-pink-500 rounded-full" />
                    Trending Stories
                  </h2>
                  <span className="text-xs uppercase tracking-widest text-gray-500 hidden sm:inline">
                    Right now on RGM
                  </span>
                </div>
                <div className="flex gap-5 overflow-x-auto pb-4 -mx-4 px-4 snap-x snap-mandatory scrollbar-thin scrollbar-thumb-purple-900">
                  {trendingStories.map((r, i) => (
                    <TrendingStoryCard key={r.report_id} report={r} rank={i + 1} />
                  ))}
                </div>
              </section>
            )}

            {/* ======= LATEST GRID ======= */}
            {latestStories.length > 0 && (
              <section className="mb-12" data-testid="reports-latest-section">
                <div className="flex items-end justify-between mb-5">
                  <h2 className="text-2xl md:text-3xl font-black text-white tracking-tight flex items-center gap-3">
                    <span className="inline-block w-1 h-8 bg-gradient-to-b from-purple-400 to-fuchsia-500 rounded-full" />
                    Latest
                  </h2>
                  <span className="text-xs uppercase tracking-widest text-gray-500 hidden sm:inline">
                    Fresh off the desk
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="reports-grid">
                  {latestStories.map((r) => (
                    <LatestStoryCard key={r.report_id} report={r} />
                  ))}
                </div>
              </section>
            )}

            {/* ======= RELATED / MORE STORIES ======= */}
            {relatedStories.length > 0 && (
              <section data-testid="reports-related-section">
                <div className="flex items-end justify-between mb-5">
                  <h2 className="text-2xl md:text-3xl font-black text-white tracking-tight flex items-center gap-3">
                    <span className="inline-block w-1 h-8 bg-gradient-to-b from-emerald-400 to-cyan-500 rounded-full" />
                    More Stories
                  </h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {relatedStories.map((r) => (
                    <RelatedStoryRow key={r.report_id} report={r} />
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default ReportsPage;
